import {
  startOfDay,
  startOfWeek,
  startOfMonth,
  startOfQuarter,
  startOfYear,
  endOfDay,
  endOfWeek,
  endOfMonth,
  endOfQuarter,
  endOfYear,
  eachDayOfInterval,
  eachWeekOfInterval,
  eachMonthOfInterval,
  eachQuarterOfInterval,
  eachYearOfInterval,
  format,
  parseISO,
} from "date-fns";

export type DateGranularity = "day" | "week" | "month" | "quarter" | "year";

interface DateRange {
  start: Date;
  end: Date;
}

interface GroupedPeriod {
  periodStart: Date;
  displayValue: string;
  count: number;
}

export const getDateRangeFromData = (dates: string[]): DateRange => {
  const validDates = dates
    .map((d) => parseISO(d))
    .filter((d) => !isNaN(d.getTime()))
    .sort((a, b) => a.getTime() - b.getTime());

  if (validDates.length === 0) {
    const now = new Date();
    return { start: now, end: now };
  }

  return {
    start: validDates[0],
    end: validDates[validDates.length - 1],
  };
};

export const getPeriodStart = (
  date: Date,
  granularity: DateGranularity
): Date => {
  switch (granularity) {
    case "day":
      return startOfDay(date);
    case "week":
      return startOfWeek(date, { weekStartsOn: 1 }); // Start week on Monday
    case "month":
      return startOfMonth(date);
    case "quarter":
      return startOfQuarter(date);
    case "year":
      return startOfYear(date);
  }
};

export const getPeriodEnd = (
  date: Date,
  granularity: DateGranularity
): Date => {
  switch (granularity) {
    case "day":
      return endOfDay(date);
    case "week":
      return endOfWeek(date, { weekStartsOn: 1 }); // End week on Sunday
    case "month":
      return endOfMonth(date);
    case "quarter":
      return endOfQuarter(date);
    case "year":
      return endOfYear(date);
  }
};

export const formatPeriod = (
  date: Date,
  granularity: DateGranularity
): string => {
  switch (granularity) {
    case "day":
      return format(date, "MMM d, yyyy");
    case "week":
      return `Week of ${format(date, "MMM d, yyyy")}`;
    case "month":
      return format(date, "MMMM yyyy");
    case "quarter":
      return `Q${Math.floor(date.getMonth() / 3) + 1} ${date.getFullYear()}`;
    case "year":
      return format(date, "yyyy");
  }
};

export const getAllPeriods = (
  range: DateRange,
  granularity: DateGranularity
): Date[] => {
  const start = getPeriodStart(range.start, granularity);
  const end = getPeriodEnd(range.end, granularity);

  switch (granularity) {
    case "day":
      return eachDayOfInterval({ start, end });
    case "week":
      return eachWeekOfInterval({ start, end }, { weekStartsOn: 1 });
    case "month":
      return eachMonthOfInterval({ start, end });
    case "quarter":
      return eachQuarterOfInterval({ start, end });
    case "year":
      return eachYearOfInterval({ start, end });
  }
};

export const fillMissingPeriods = (
  groupedData: Map<string, number>,
  dateField: string,
  granularity: DateGranularity,
  records: any[]
): GroupedPeriod[] => {
  // Get the date range from the actual data
  const dates = records.map((record) => record[dateField]).filter(Boolean);

  const dateRange = getDateRangeFromData(dates);
  const allPeriods = getAllPeriods(dateRange, granularity);

  // Create a complete set of periods with counts (0 for missing periods)
  return allPeriods.map((periodStart) => {
    const displayValue = formatPeriod(periodStart, granularity);
    return {
      periodStart,
      displayValue,
      count: groupedData.get(displayValue) ?? 0,
    };
  });
};
